import { useRef, useState } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { toast } from "sonner";
import { Paperclip, Link2, Crop, Trash2, Loader2, X, GripVertical, Sheet, FileText } from "lucide-react";
import api from "../../lib/api";
import { sourceImage, isVisual } from "./AttachmentsView";

const small = "bg-brand-page border border-brand-border rounded px-2 py-1 text-xs text-brand-ink focus:outline-none focus:border-brand-primary";

function CropDialog({ a, onSave, onClose }) {
  const img = useRef(null);
  const [crop, setCrop] = useState(a.crop ? { unit: "%", x: a.crop.x * 100, y: a.crop.y * 100, width: a.crop.w * 100, height: a.crop.h * 100 } : undefined);
  const save = () => {
    const el = img.current;
    const aspect = el.naturalWidth / el.naturalHeight;
    const c = crop && crop.width > 1 && crop.height > 1 ? { x: crop.x / 100, y: crop.y / 100, w: crop.width / 100, h: crop.height / 100 } : null;
    onSave({ crop: c, aspect });
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="report-crop-dialog">
      <div className="bg-brand-page rounded-xl max-w-4xl w-full max-h-[92vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <p className="font-serif text-xl text-brand-ink">Crop {a.kind === "pdf" ? `page ${a.page + 1}` : "image"}</p>
          <button onClick={onClose}><X className="h-5 w-5 text-brand-muted" /></button>
        </div>
        <p className="text-xs text-brand-muted mb-3">Drag to select the area to show in the report.</p>
        <ReactCrop crop={crop} onChange={(_, pct) => setCrop(pct)}>
          <img ref={img} src={sourceImage(a)} alt="" crossOrigin="anonymous" className="max-h-[65vh]" data-testid="report-crop-image" />
        </ReactCrop>
        <div className="flex gap-2 mt-4">
          <button onClick={save} data-testid="report-crop-save" className="rounded-full bg-brand-ink text-brand-page px-4 py-2 text-sm">Save crop</button>
          <button onClick={() => { setCrop(undefined); onSave({ crop: null, aspect: a.aspect }); }} data-testid="report-crop-reset" className="rounded-full border border-brand-border px-4 py-2 text-sm">Show full</button>
        </div>
      </div>
    </div>
  );
}

const DND_TYPE = "application/x-bond-attachment";

export function AttachmentEditor({ reportId, items = [], onChange, testid, dndKey, onMove }) {
  const [busy, setBusy] = useState(false);
  const [cropping, setCropping] = useState(null);
  const [over, setOver] = useState(null);
  const [armed, setArmed] = useState(null);
  const canDnd = !!(dndKey && onMove);
  const dragStart = (e, i) => { e.dataTransfer.setData(DND_TYPE, JSON.stringify({ key: dndKey, index: i })); e.dataTransfer.effectAllowed = "move"; };
  const slot = (e, i) => {
    if (i >= items.length) return items.length;
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientY > r.top + r.height / 2 ? i + 1 : i;
  };
  const dragOver = (e, i) => { if (!e.dataTransfer.types.includes(DND_TYPE)) return; e.preventDefault(); e.stopPropagation(); setOver(slot(e, i)); };
  const drop = (e, i) => {
    e.preventDefault(); e.stopPropagation(); setOver(null);
    const raw = e.dataTransfer.getData(DND_TYPE);
    if (raw) onMove(JSON.parse(raw), { key: dndKey, index: slot(e, i) });
  };
  const set = (i, patch) => onChange(items.map((a, j) => (j === i ? { ...a, ...patch } : a)));

  const upload = async (files) => {
    if (!reportId) { toast.error("Save the report once before adding files"); return; }
    setBusy(true);
    const added = [];
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try { const { data } = await api.post(`/reports/${reportId}/files`, fd); added.push(data); }
      catch (e) { toast.error(`${f.name}: ${e?.response?.data?.detail || "upload failed"}`); }
    }
    setBusy(false);
    if (added.length) onChange([...items, ...added]);
  };
  const addLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) { toast.error("Links must start with http:// or https://"); return; }
    const title = window.prompt("Link text", url.replace(/^https?:\/\//, "").split("/")[0]) || url;
    onChange([...items, { id: `l${Date.now()}`, kind: "link", url, title, caption: "", display: "link", page: 0, page_count: 1 }]);
  };

  return (
    <div className={`mt-2 space-y-2 rounded transition-colors ${canDnd && over === items.length ? "bg-brand-primary/5 outline-dashed outline-1 outline-brand-primary" : ""}`} data-testid={`${testid}-attachments`}
      onDragOver={canDnd ? (e) => dragOver(e, items.length) : undefined} onDragLeave={() => setOver(null)} onDrop={canDnd ? (e) => drop(e, items.length) : undefined}>
      {items.map((a, i) => (
        <div key={a.id} draggable={canDnd && armed === i} onDragStart={(e) => dragStart(e, i)} onDragEnd={() => { setOver(null); setArmed(null); }}
          onDragOver={canDnd ? (e) => dragOver(e, i) : undefined} onDrop={canDnd ? (e) => drop(e, i) : undefined}
          className={`flex items-center gap-2 rounded border bg-brand-page p-1.5 transition-colors ${over === i ? "border-brand-border border-t-2 border-t-brand-primary" : over === i + 1 && i === items.length - 1 ? "border-brand-border border-b-2 border-b-brand-primary" : "border-brand-border"}`} data-testid={`${testid}-att-${i}`}>
          {canDnd && <GripVertical className="h-4 w-4 text-brand-tertiary shrink-0 cursor-grab active:cursor-grabbing" onMouseDown={() => setArmed(i)} onMouseUp={() => setArmed(null)} data-testid={`${testid}-att-handle-${i}`} />}
          {!isVisual(a) ? (() => { const I = a.kind === "link" ? Link2 : a.kind === "sheet" ? Sheet : FileText; return <I className="h-8 w-8 p-2 text-brand-gold shrink-0" />; })() : <img src={sourceImage(a)} alt="" className="h-10 w-10 object-cover rounded shrink-0" crossOrigin="anonymous" />}
          <div className="flex-1 min-w-0 space-y-1">
            <input value={["link", "file"].includes(a.kind) ? a.title : a.caption} onChange={(e) => set(i, ["link", "file"].includes(a.kind) ? { title: e.target.value } : { caption: e.target.value })}
              placeholder={["link", "file"].includes(a.kind) ? "Link text" : "Caption"} className={`${small} w-full`} data-testid={`${testid}-att-caption-${i}`} />
            {(isVisual(a) || a.kind === "sheet") && (
              <div className="flex items-center gap-1.5">
                <select value={a.display} onChange={(e) => set(i, { display: e.target.value })} className={small} data-testid={`${testid}-att-display-${i}`}>
                  <option value="inline">{a.kind === "sheet" ? "Show as table" : "Show inline"}</option><option value="link">Link only</option>
                </select>
                {a.kind === "pdf" && a.page_count > 1 && (
                  <select value={a.page} onChange={(e) => set(i, { page: Number(e.target.value), crop: null })} className={small} data-testid={`${testid}-att-page-${i}`}>
                    {[...Array(a.page_count)].map((_, p) => <option key={p} value={p}>Page {p + 1}</option>)}
                  </select>
                )}
                {a.display === "inline" && isVisual(a) && <button onClick={() => setCropping(i)} data-testid={`${testid}-att-crop-${i}`} className="inline-flex items-center gap-1 text-[11px] text-brand-primary"><Crop className="h-3 w-3" />{a.crop ? "Re-crop" : "Crop"}</button>}
              </div>
            )}
          </div>
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${testid}-att-remove-${i}`} className="p-1 text-brand-tertiary hover:text-brand-neg"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <div className="flex gap-3">
        <label className="inline-flex items-center gap-1 text-[11px] text-brand-primary cursor-pointer" data-testid={`${testid}-att-upload`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />} File
          <input type="file" multiple className="hidden" onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} data-testid={`${testid}-att-input`} />
        </label>
        <button onClick={addLink} data-testid={`${testid}-att-add-link`} className="inline-flex items-center gap-1 text-[11px] text-brand-primary"><Link2 className="h-3 w-3" /> Link</button>
      </div>
      {cropping !== null && items[cropping] && (
        <CropDialog a={items[cropping]} onClose={() => setCropping(null)} onSave={(patch) => { set(cropping, patch); setCropping(null); }} />
      )}
    </div>
  );
}
